// @ts-check
import { setFilterGroup } from 'actions/organisation';
import { getFilter } from 'selectors/organisation';
import {
  CORE_ACTIVITY_CODE,
  getPresetFilterDefaults,
} from 'components/Search/Filters/presetSideEffects';
import { coreFlagForActivityCode } from 'components/Search/Filters/fundingFilterRules';

// SCIL-17770: org-side adapter for the KOL / Core-Labs presets. It consumes the
// SAME preset DEFINITION data as main search — `getPresetFilterDefaults` and
// `CORE_ACTIVITY_CODE` from presetSideEffects — so there is one source of truth
// for the expansion values. Only the wiring differs: main search dispatches
// against the `search` slice; here we dispatch against the shared
// `filters[orgId]` slice via the org actions/selectors.

/**
 * Apply KOL / Core-Labs preset side-effects to an organisation's shared filter
 * slice. Mirrors applyPresetSideEffects (search slice) 1:1 in behaviour:
 *  - KOL on fills totalPublications / threeYearHIndexAverage defaults, only
 *    where the caller has not already provided them.
 *  - Core on/off adds/removes the P30 activity code from nihActivityCode.
 *
 * @param {number} organisationId
 * @param {Record<string, any>} filters the preset intent (e.g. { kol: true })
 */
/**
 * Mirror of the main search Activity Code handler: the Core Labs Only flag
 * follows whether the P30 code is among the selected activity codes.
 *
 * @param {number} organisationId
 * @param {string[] | null} value the new nihActivityCode selection
 */
export const applyOrgActivityCodeChange = (
  organisationId,
  value
) => dispatch => {
  dispatch(
    setFilterGroup(organisationId, 'core', coreFlagForActivityCode(value))
  );
  dispatch(setFilterGroup(organisationId, 'nihActivityCode', value));
};

export const applyOrgPresetSideEffects = (organisationId, filters) => (
  dispatch,
  getState
) => {
  const defaults = getPresetFilterDefaults(filters);
  for (const [key, value] of Object.entries(defaults)) {
    dispatch(setFilterGroup(organisationId, key, value));
  }

  if (filters?.core === undefined) return;

  const current =
    getFilter(organisationId, 'nihActivityCode')(getState()) || [];
  const hasP30 = current.includes(CORE_ACTIVITY_CODE);
  const shouldHaveP30 = filters.core === true;

  if (shouldHaveP30 && !hasP30) {
    dispatch(
      setFilterGroup(organisationId, 'nihActivityCode', [
        ...current,
        CORE_ACTIVITY_CODE,
      ])
    );
  } else if (!shouldHaveP30 && hasP30) {
    const next = current.filter(v => v !== CORE_ACTIVITY_CODE);
    dispatch(
      setFilterGroup(
        organisationId,
        'nihActivityCode',
        next.length ? next : null
      )
    );
  }
};
